import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as _ from 'underscore';

import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { faPrint, faQuestionCircle, faFileExcel, faSpinner, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { faUncharted } from '@fortawesome/free-brands-svg-icons';

import { diagnosticIntroSteps } from './diagnostic.intro-steps';

import { SessionStorageService } from '../../../core/services/session-storage.service';
import { ManagementUser } from '../../../core/models/management-user.model';
import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { SchoolClass } from '../../../core/models/school-class.model';
import { SubscriptionTypes } from '../../../core/models/subscription-types.model';
import { Student } from '../../../core/models/student.model';
import { ReportsService } from '../../../core/services/reports.service';
import { StudentsFilterComponent } from '../../../shared/components/students-filter/students-filter.component';
import { StudentsService } from '../../../core/services/students.service';
import { AssessmentScore } from '../../../core/models/assessment-score.model';
import { ScoreCutoffs } from '../../../core/models/score-cutoffs.model';
import { PrintService } from 'src/app/core/services/print.service';
import { UtilityService } from '../../../core/services/utility.service';
import { canViewCoTeacherFilter, canViewSchoolFilter } from '../../utils';
import { BatchPrintOptions } from '../../../core/models/print-opts';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import * as Sentry from '@sentry/angular';

@Component({
    selector: 'wf-diagnostic-report',
    templateUrl: './diagnostic.component.html',
    styleUrls: ['./diagnostic.component.css'],
    standalone: false
})
export class DiagnosticReportComponent implements OnInit, AfterViewInit {
  currentUser: ManagementUser | null = null ;
  isSystem: boolean = false ;
  isElementary: boolean = false ;
  showArchived: boolean = false ;
  showSchoolFilter: boolean = false ;
  schoolFilterError: string = '' ;
  showCoTeacherFilter: boolean = false ;
  hideSharedStudents: boolean = false ;
  productRoute: string = '' ;
  automaticityTitle: string = 'Word Recognition' ;
  tierLabels: { [key: string]: string } = {} ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  grades: string[] = [] ;
  classes: SchoolClass[] = [] ;
  testWindows: string[] = [] ;
  testWindowsCompare: string[] = [] ;
  students: Student[] = [] ;
  filteredStudents: Student[] = [] ;
  scoreTotalsWindow1: any = {} ;
  scoreTotalsWindow2: any = {} ;
  automaticityData: number[] = [ 0, 0, 0 ] ;
  decodingData: number[] = [ 0, 0, 0 ] ;
  selectedProduct: string = '' ;
  sortColumn: string = '' ;
  sortReverse: boolean = false ;
  compareTestWindows: boolean = false ;
  selectedTestWindow: string = '' ;
  selectedCompareTestWindow: string = '' ;
  navigateStudentId: number = -1 ;
  exportingReport: boolean = false ;
  exportError: string = '' ;
  filterGrade: string = '';
  filterClassId: string = '0';

  reportsIcon = CATEGORY_ICONS['class-reports'];

  // Status tracking for pie chart legend totals
  statusCounts: { notStarted: number; started: number; skipped: number } = { notStarted: 0, started: 0, skipped: 0 };

  chartType: ChartType = 'pie' ;
  chartData: ChartData<'pie', number[], string | string[]> = {
    labels: [ 'Proficient', 'Some Risk', 'High Risk' ],
    datasets: [{
      data: [ this.automaticityData[0], this.automaticityData[1], this.automaticityData[2] ],
      backgroundColor: [
        '#58B957',
        '#F2AE43',
        '#DB524B'
      ],
      hoverBackgroundColor: [
        '#58B957',
        '#F2AE43',
        '#DB524B'
      ],
    }]
  } ;
  chartOptions: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (toolTip) => {
            //let totalStudents: number = toolTip.dataset['data'].reduce((prev: number, curr: number) => { return prev! + curr! }, 0) ;
            let totalStudents = 0 ;
            toolTip.dataset['data'].forEach((val: any) => totalStudents += (val as number)) ;

            return (totalStudents === 0) ? '' : `${Math.round((toolTip.parsed / totalStudents) * 100)}%` ;
          }
        }
      }
    },
  } ;
  printIcon = faPrint ;
  excelIcon = faFileExcel ;
  loadingIcon = faSpinner ;
  diagnosticIcon = faUncharted ;
  helpIcon = faQuestionCircle;
  batchPrintIcon = faLayerGroup ;

  readonly tourSteps = diagnosticIntroSteps ;

  readonly showBatchPrintDialog = signal<boolean>(false);
  readonly isGeneratingBatchPrint = signal<boolean>(false);
  readonly showPdfPreview = signal<boolean>(false);
  readonly batchPdfUrl = signal<string | null>(null);

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective ;
  @ViewChild(StudentsFilterComponent) studentFilter?: StudentsFilterComponent ;
  @ViewChild('printContent') printContent!: ElementRef<HTMLElement>;
  @ViewChild('automaticityPieCanvas') automaticityPieCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('automaticitySummaryImg') automaticitySummaryImg!: ElementRef<HTMLImageElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reportsService: ReportsService,
    private studentsService: StudentsService,
    private sessionStorageService: SessionStorageService,
    private changeDetector: ChangeDetectorRef,
    private printService: PrintService,
    protected utilityService: UtilityService
  ) {
    this.navigateStudentId = parseInt(this.router.getCurrentNavigation()?.extras?.state?.['studentId']) ;
    if (isNaN(this.navigateStudentId)) this.navigateStudentId = -1 ;
  }

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.showCoTeacherFilter = canViewCoTeacherFilter(this.currentUser);

    if (this.route.snapshot.data['resolveData'].erred)
    {
      this.schoolFilterError = this.route.snapshot.data['resolveData'].message ;
    }
    else
    {
      this.students = this.route.snapshot.data['resolveData'].students ;
      this.grades = this.route.snapshot.data['resolveData'].grades ;
      this.classes = this.route.snapshot.data['resolveData'].classes ;
      this.testWindows = this.route.snapshot.data['resolveData'].testWindows ;
      this.testWindowsCompare = this.route.snapshot.data['resolveData'].compareTestWindows ;
      this.filteredStudents = this.students ;
      this.filterGrade = this.grades[0].toLowerCase();
      this.filterClassId = `${this.classes[0].schoolClassID}`;
    }

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts ;
    this.schools = this.route.snapshot.data['filterData'].schools ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;

    if (this.currentUser!.isFILUser() || this.currentUser!.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    if (canViewSchoolFilter(this.currentUser))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.unshift(ManagementUser.getGenericUser()) ;
    }

    // Setup our selected product
    // There seems like duplication here, but productRoute is used for routing, and we want to keep this separate from
    // the selectedProduct, which is used in displays and might change for display reasons (rebranding, etc) and should
    // not affect the route URL
    this.isSystem = this.route.snapshot.data['product'] === SubscriptionTypes.FullProduct ;
    this.selectedProduct = this.route.snapshot.data['product']
    this.productRoute = (this.isSystem) ? 'system' : 'diagnostic' ;

    // Prime our selected test windows and archive options
    if (this.sessionStorageService.getDiagnosticOpts() === null)
    {
      // Initialize our diagnostic report options
      let opts: { [key: string] : any } = {} ;
      opts[SubscriptionTypes.FullProduct] = {
        'showArchived': false,
        'showCompare': false,
        'testWindow': null,
        'compareWindow': null,
      } ;
      opts[SubscriptionTypes.DiagnosticProduct] = {
        'showArchived': false,
        'showCompare': false,
        'testWindow': null,
        'compareWindow': null,
      } ;
      this.sessionStorageService.setDiagnosticOpts(opts) ;
    }
    this.showArchived = this.sessionStorageService.getDiagnosticOpts()[this.selectedProduct]['showArchived'] ;

    let compareWindowIdx = (this.testWindows.length > 2) ? 1 : 0 ;
    let window = this.sessionStorageService.getDiagnosticOpts()[this.selectedProduct]['testWindow'] ;
    let compareWindow = this.sessionStorageService.getDiagnosticOpts()[this.selectedProduct]['compareWindow'] ;
    this.selectedTestWindow = (window !== null) ? window : 'All Tests' ;
    this.selectedCompareTestWindow = (compareWindow !== null) ? compareWindow : this.testWindowsCompare[compareWindowIdx] ;

    // Before we set our diagnostic students, we need to make sure and process the scores
    // NOTE: This is a bit wasteful in that we do not need to populate the report data here,
    //     : but this method goes through assessments and tally's and assigns instructional priorities
    this.reportsService.processStudentsAssessmentScores(this.students, {}, { 'testWindow': this.selectedTestWindow }, { 'testWindow': this.selectedCompareTestWindow }) ;

    // NOTE: This is a bit of a hack and work around for the fact that to view any students detailed diagnostic data,
    //     : all the data is loaded and put on the session HERE first, in the general diagnostic report. So if we are linking
    //     : directly to see a students detailed diagnostics (from say the Student Summary report), we need to stop here first
    //     : to load and process the data, then forward the visitor on. Ideally this is reworked so that the detailed diagnostic
    //     : report can handle all the data loading, I dont' think as it was originally designed there was thought to viewing it
    //     : stand-alone, as there was no other way to get to the detailed diagnostic except from the general diagnostic
    if (this.navigateStudentId > 0)
    {
      this.filterResults({ grade: 'all', class: 0, extraSupport: 'all' }) ;
      this.goToDiagnosticDetails(this.navigateStudentId, true) ;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const filterSettings = this.studentFilter?.getFilterSettings();
      this.filterResults(filterSettings) ;
      this.setReportSummaryData(this.filteredStudents) ;
    }) ;
  }

  filterResults(filterOpts: any) {
    // Handle case where filterOpts is undefined
    if (!filterOpts) {
      filterOpts = { grade: 'all', class: 0, extraSupport: 'all' };
    }

    // Store our filters for any exports
    this.filterGrade = filterOpts.grade?.toLowerCase() ?? '';
    this.filterClassId = filterOpts.class ?? '0';

    // Grade value that comes from the filter is our true, displayed grade (not our internal grade, so 4 is 4th grade)
    let gradeNum = parseInt(filterOpts.grade) ;
    let selectedGradeGroup = (filterOpts.grade === 'four_below_all' || (!isNaN(gradeNum) && gradeNum <= 4)) ? 'elementary' : 'secondary' ;
    if (filterOpts.grade === 'all')
    {
      this.tierLabels = {
        'proficient' : '',
        'some-risk' : '',
        'high-risk' : '',
      } ;
    }
    else
    {
      this.tierLabels = {
        'proficient' : ScoreCutoffs[selectedGradeGroup]['labels']['topTier']['range'],
        'some-risk' : ScoreCutoffs[selectedGradeGroup]['labels']['middleTier']['range'],
        'high-risk' : ScoreCutoffs[selectedGradeGroup]['labels']['bottomTier']['range'],
      } ;
    }
    this.isElementary = (filterOpts.grade === 'four_below_all' || (!isNaN(gradeNum) && gradeNum <= 4)) ;

    let students = this.filterCoAccessStudents(this.studentsService.filterStudents(JSON.parse(JSON.stringify(this.students)), filterOpts)) ;
    this.filteredStudents = [] ;

    // We need to filter our students based on if we are comparing test windows, or if we are just filtering on results
    // (we do not care about the selected test type).
    if (this.compareTestWindows)
    {
      let sameCompareWindows = (this.selectedCompareTestWindow === this.selectedTestWindow) ;
      students.forEach((student) => {
        if (student.enabled || this.showArchived)
        {
          // If we are looking for a specific test type (test window), we need to reset the student assessments
          // and only add the ones that match our filtered test window
          let foundAssessmentScore = false ;
          let assessmentScores: AssessmentScore[] = [] ;

          student.assessmentScores?.forEach((assessment) => {
            if (assessment.dateCompleted && ((assessment.testWindow === this.selectedTestWindow) || (assessment.testWindow === this.selectedCompareTestWindow)))
            {
              foundAssessmentScore = true ;
              assessmentScores.push(assessment) ;
            }
          }) ;

          // Include students with subscriptions even if they don't have assessment data
          // Only exclude students who don't have any subscription (enrolledSubscription = 0)
          if (assessmentScores.length === 2 || (foundAssessmentScore && sameCompareWindows))
          {
            student.assessmentScores = assessmentScores ;
            this.filteredStudents.push(student) ;
          }
        }
      }) ;

    }
    else
    {
      // Filter results
      if (this.selectedTestWindow === 'All Tests')
      {
        let studentsWithSubscriptionNoData = 0;
        let studentsWithSubscriptions = 0;

        // We are filtering for all tests, so add the student if it is enabled (or if we are showing archived)
        students.forEach((student) => {
          if (student.enrolledSubscription > 0) {
            studentsWithSubscriptions++;
          }

          if (student.enabled || this.showArchived) {
            // Track students with subscriptions but no assessment data
            if (student.enrolledSubscription > 0 && (!student.assessmentScores || student.assessmentScores.length === 0)) {
              studentsWithSubscriptionNoData++;
            }

            this.filteredStudents.push(student);
          }
        });

      }
      else
      {
        students.forEach((student) => {
          if (!student.enabled && !this.showArchived) return ;

          let foundAssessmentScore = false ;
          let assessmentScores: AssessmentScore[] = [] ;

          student.assessmentScores?.forEach((assessment) => {
            if (assessment.testWindow === this.selectedTestWindow)
            {
              foundAssessmentScore = true ;
              assessmentScores.push(assessment) ;
            }
          }) ;

          // Include students with subscriptions even if they don't have assessment data
          // Only exclude students who don't have any subscription (enrolledSubscription = 0)
          if (foundAssessmentScore || student.enrolledSubscription > 0)
          {
            student.assessmentScores = assessmentScores ;
            this.filteredStudents.push(student) ;
          }
        }) ;
      }
    }

    this.setReportSummaryData(this.filteredStudents) ;
    this.sessionStorageService.setDiagnosticDetailsStudentIds(this.filteredStudents.map(student => student.userID)) ;
  }

  filterCoAccessStudents(students: Student[]) {
    const teacherId = this.reportsService.getSelectedTeacherForReports().userID;
    // If 'All Teachers' is selected, then show all students
    if (!this.showCoTeacherFilter && teacherId !== 0) {
      return students.filter(student => student.teacherID === this.reportsService.getSelectedTeacherForReports().userID) ;
    } else if (this.hideSharedStudents && teacherId !== 0) {
      return students.filter(student => student.teacherID === this.reportsService.getSelectedTeacherForReports().userID) ;
    } else {
      return students ;
    }
  }

  filterSchoolTeacher() {
    this.reportsService.getDiagnosticData(this.isSystem).subscribe({
      next: (data: any) => {
        if (data.erred)
        {
          this.schoolFilterError = data.message ;
        }
        else
        {
          this.schoolFilterError = '' ;
          this.students = data.students ;
          this.grades = data.grades ;
          this.classes = data.classes ;
          this.testWindows = data.testWindows ;
          this.testWindowsCompare = data.compareTestWindows ;
          this.filteredStudents = this.students ;
          this.changeDetector.detectChanges();

          let compareWindowIdx = (this.testWindows.length > 2) ? 1 : 0 ;
          let window = this.sessionStorageService.getDiagnosticOpts()[this.selectedProduct]['testWindow'] ;
          let compareWindow = this.sessionStorageService.getDiagnosticOpts()[this.selectedProduct]['compareWindow'] ;
          this.selectedTestWindow = (window !== null) ? window : 'All Tests' ;
          this.selectedCompareTestWindow = (compareWindow !== null) ? compareWindow : this.testWindowsCompare[compareWindowIdx] ;

          // Before we set our diagnostic students, we need to make sure and process the scores
          // NOTE: This is a bit wasteful in that we do not need to populate the report data here,
          //     : but this method goes through assessments and tally's and assigns instructional priorities
          this.reportsService.processStudentsAssessmentScores(this.students, {}, { 'testWindow': this.selectedTestWindow }, { 'testWindow': this.selectedCompareTestWindow }) ;

          this.studentFilter?.resetFilterSettings() ;
          this.filterResults(this.studentFilter?.getFilterSettings()) ;
        }
      }
    }) ;
  }

  hasCompletedAssessments(student: Student) {
    return student.assessmentScores?.some(assessment => !!assessment.dateCompleted);
  }

  sortTable(col: string) {
    if (col === 'dateCompleted' && (this.selectedTestWindow === 'All Tests' || this.compareTestWindows)) {
      // Do not sort by date when "All Tests" is selected
      return;
    }

    if (this.sortColumn === col) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortReverse = false;
      this.sortColumn = col;
    }

    this.filteredStudents = (this.sortReverse)
      ? _.sortBy(this.filteredStudents, this.sortFunction, this).reverse()
      : _.sortBy(this.filteredStudents, this.sortFunction, this);
  }

  // Called whenever the test window opts are changed (window/compare/compare window)
  testWindowChanged() {
    let opts = this.sessionStorageService.getDiagnosticOpts() ;
    opts[this.selectedProduct] = {
      'showArchived': this.showArchived,
      'showCompare': this.compareTestWindows,
      'testWindow': this.selectedTestWindow,
      'compareWindow': this.selectedCompareTestWindow,
    }
    this.sessionStorageService.setDiagnosticOpts(opts) ;

    if (this.selectedTestWindow === 'All Tests')
    {
      this.compareTestWindows = false ;
    }

    this.filterResults(this.studentFilter?.getFilterSettings()) ;
  }

  goToDiagnosticDetails(userId: number, skipLocationChange: boolean = false) {
    let studentIdx = this.filteredStudents.findIndex(student => student.userID === userId) ;
    let navExtras = {} ;

    if (studentIdx === -1) return ;

    if (skipLocationChange) {
      navExtras = {
        skipLocationChange: true,
      }
    }

    // NOTE: In the future, we should simplify the routing for the diagnostic details so that we don't need
    //     : to specify what the product/category/etc is, we know that based on the student we are showing
    //     : data for
    this.sessionStorageService.setDiagnosticDetailsStudentId(this.filteredStudents[studentIdx].userID) ;
    this.router.navigate([ '/reports/', this.productRoute, 'diagnostic', ((this.filteredStudents[studentIdx].grade <= 5) ? 'elementary' : 'secondary'), 'detail', userId], navExtras) ;
  }

  getDiagnosticClass(isElementary: boolean, score: number) {
    return this.reportsService.getDiagnosticClass((isElementary ? 'elementary' : 'secondary'), score) ;
  }

  getDiagnosticImage(isElementary: boolean, score: number) {
    return this.reportsService.getDiagnosticImage((isElementary ? 'elementary' : 'secondary'), score) ;
  }

  exportDiagnosticResults() {
    this.exportError = '' ;
    this.exportingReport = true ;
    this.reportsService.downloadDiagnosticResults(
      this.reportsService.getSelectedSchoolForReports().schoolID,
      this.reportsService.getSelectedTeacherForReports().userID,
      this.filterClassId,
      this.filterGrade,
      this.isSystem,
      this.selectedTestWindow,
      this.showArchived,
      this.compareTestWindows,
      this.selectedCompareTestWindow
    ).subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a') ;
        const objectUrl = URL.createObjectURL(blob) ;
        a.href = objectUrl ;
        a.download = 'diagnostic_results.xlsx' ;
        a.click() ;
        URL.revokeObjectURL(objectUrl) ;
        this.exportingReport = false ;
      },
      error: (err: any) => {
        this.exportingReport = false ;
        this.exportError = 'There was an error trying to export the Diagnostic Results report. Please contact support for assistance.' ;
        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'diagnostic-results',
            action: 'export',
            schoolId: this.reportsService.getSelectedSchoolForReports().schoolID,
            teacherId: this.reportsService.getSelectedTeacherForReports().userID,
          }
        }) ;
      }
    }) ;
  }

  openBatchPrintDialog() {
    this.showBatchPrintDialog.set(true);
  }

  closeBatchPrintDialog() {
    this.showBatchPrintDialog.set(false);
  }

  closeBatchPdfPreview() {
    if (this.batchPdfUrl()) {
      URL.revokeObjectURL(this.batchPdfUrl()!);
    }
    this.showPdfPreview.set(false);
    this.batchPdfUrl.set(null);
  }

  batchPrint(data: BatchPrintOptions) {
    if (!data.studentIds || data.studentIds.length === 0) return;

    const teacherId = this.reportsService.getSelectedTeacherForReports().userID;
    this.isGeneratingBatchPrint.set(true);
    this.reportsService.batchPrintDiagnosticDetail(teacherId, data, this.isSystem).subscribe({
      next: (blob: Blob) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        this.isGeneratingBatchPrint.set(false);
        this.closeBatchPrintDialog();
        this.batchPdfUrl.set(url);
        this.showPdfPreview.set(true);
      },
      error: () => {
        this.isGeneratingBatchPrint.set(false);
      }
    });
  }

  printSummary() {
    let opts = this.sessionStorageService.getDiagnosticOpts() ;
    if (this.selectedTestWindow !== 'All Tests' && !opts[this.selectedProduct].showCompare) {
      this.reportsService.convertCanvasToImage(this.automaticityPieCanvas.nativeElement, this.automaticitySummaryImg.nativeElement);
    }
    let school = this.reportsService.getSelectedSchoolForReports().name;
    let teacher = this.reportsService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }

  /**
   * Get diagnostic status for a student
   * @param student Student object
   * @param testWindow Optional test window to check for specific window status
   * @returns Status string: 'Not Started', 'In Progress', 'Completed', 'Skipped'
   */
  getDiagnosticStatus(student: Student, testWindow?: string): string {
    if (!student.assessmentScores || student.assessmentScores.length === 0) {
      return student.enrolledSubscription > 0 ? 'Not Started' : '-';
    }

    // If specific test window is provided, check only that window
    if (testWindow && testWindow !== 'All Tests') {
      const assessment = student.assessmentScores.find(a => a.testWindow === testWindow);
      if (!assessment) {
        return student.enrolledSubscription > 0 ? 'Not Started' : '-';
      }
      return this.getAssessmentStatus(assessment);
    }

    // For All Tests or general status, show status of latest assessment
    const latestAssessment = student.assessmentScores[student.assessmentScores.length - 1];
    return this.getAssessmentStatus(latestAssessment);
  }

  /**
   * Get status for a specific assessment
   */
  private getAssessmentStatus(assessment: AssessmentScore): string {
    if (assessment.skipped) {
      return 'Skipped';
    }

    if (assessment.dateCompleted) {
      return 'Completed';
    }

    if (assessment.dateStarted) {
      return 'In Progress';
    }

    if (assessment.dateScheduled) {
      return 'Scheduled';
    }

    return 'Not Started';
  }

  /**
   * Get progress percentage for a diagnostic assessment
   * @param student Student object
   * @param testWindow Optional test window to check for specific window progress
   * @returns Progress percentage string like "75%" or "-"
   */
  getDiagnosticProgress(student: Student, testWindow?: string): string {
    if (!student.assessmentScores || student.assessmentScores.length === 0) {
      return '-';
    }

    // If specific test window is provided, check only that window
    if (testWindow && testWindow !== 'All Tests') {
      const assessment = student.assessmentScores.find(a => a.testWindow === testWindow);
      if (!assessment) {
        return '-';
      }
      return this.getAssessmentProgress(assessment);
    }

    // For All Tests or general progress, show progress of latest assessment
    const latestAssessment = student.assessmentScores[student.assessmentScores.length - 1];
    return this.getAssessmentProgress(latestAssessment);
  }

  /**
   * Get progress percentage for a specific assessment
   */
  private getAssessmentProgress(assessment: AssessmentScore): string {
    if (assessment.skipped) {
      return 'Skipped';
    }

    if (assessment.dateCompleted) {
      return '100%';
    }

    if (assessment.numDiagnosticTasks && assessment.numDiagnosticTasks > 0) {
      const completed = assessment.numDiagnosticTasksCompleted || 0;
      const total = assessment.numDiagnosticTasks;
      const percentage = Math.round((completed / total) * 100);
      return `${percentage}%`;
    }

    return '0%';
  }

  /**
   * Get all diagnostic test windows for a student when "All Tests" is selected
   * Returns array of objects with testWindow and status
   */
  getAllTestWindowStatuses(student: Student): Array<{testWindow: string; status: string; progress: string, scheduled?: number}> {
    if (!student.assessmentScores || student.assessmentScores.length === 0) {
      // If student has subscription but no assessments, show available test windows as "Not Started"
      if (student.enrolledSubscription > 0) {
        return this.testWindows.filter(tw => tw !== 'All Tests').map(tw => ({
          testWindow: tw,
          status: 'Not Started',
          progress: '0%'
        }));
      }
      return [];
    }

    // Create map of existing assessments
    const assessmentMap = new Map<string, AssessmentScore>();
    student.assessmentScores.forEach(assessment => {
      if (assessment.testWindow) {
        assessmentMap.set(assessment.testWindow, assessment);
      }
    });

    // Return status for all available test windows
    return this.testWindows.filter(tw => tw !== 'All Tests').map(tw => {
      const assessment = assessmentMap.get(tw);
      if (assessment) {
        const status = this.getAssessmentStatus(assessment) ;
        const extra: { scheduled?: number } = {} ;
        if (status.toLowerCase() === 'scheduled' && assessment.dateScheduled)
        {
          extra.scheduled = assessment.dateScheduled ;
        }

        return {
          testWindow: tw,
          status: status,
          progress: this.getAssessmentProgress(assessment),
          ...extra,
        };
      } else {
        return {
          testWindow: tw,
          status: student.enrolledSubscription > 0 ? 'Not Started' : '-',
          progress: student.enrolledSubscription > 0 ? '0%' : '-'
        };
      }
    });
  }

  /**
   * Calculate status counts for pie chart legend totals
   */
  private calculateStatusCounts() {
    this.statusCounts = { notStarted: 0, started: 0, skipped: 0 };

    if (this.selectedTestWindow === 'All Tests' || this.compareTestWindows) {
      return;
    }

    this.filteredStudents.forEach(student => {
      const status = this.getDiagnosticStatus(student, this.selectedTestWindow);
      switch (status) {
        case 'Not Started':
          this.statusCounts.notStarted++;
          break;
        case 'In Progress':
          this.statusCounts.started++;
          break;
        case 'Skipped':
          this.statusCounts.skipped++;
          break;
        // 'Completed' students are counted in the pie chart itself
      }
    });
  }

  private sortFunction(val: any) {
    if (this.sortColumn === 'systemScore' || this.sortColumn === 'decodingScore' || this.sortColumn === 'zone') {
      return val.assessmentScores[0][this.sortColumn];
    }
    if (this.sortColumn === 'dateCompleted') {
      return new Date(val.assessmentScores[0].dateCompleted);
    }
    return val[this.sortColumn];
  }

  private setReportSummaryData(students: Student[]) {
    let scoreTotals: any = {} ;
    let scoreTotalsWindow1 = {
      'testWindow': this.selectedTestWindow,
    } ;
    let scoreTotalsWindow2 = {
      'testWindow': this.selectedCompareTestWindow,
    } ;

    this.reportsService.processStudentsAssessmentScores(students, scoreTotals, scoreTotalsWindow1, scoreTotalsWindow2) ;
    this.scoreTotalsWindow1 = scoreTotalsWindow1 ;
    this.scoreTotalsWindow2 = scoreTotalsWindow2 ;

    this.automaticityData = [
      scoreTotals.automaticityProficient,
      scoreTotals.automaticitySomeRisk,
      scoreTotals.automaticityHighRisk,
    ] ;
    this.decodingData = [
      scoreTotals.decodingProficient,
      scoreTotals.decodingSomeRisk,
      scoreTotals.decodingHighRisk,
    ] ;

    this.chartData.datasets[0].data = [ this.automaticityData[0], this.automaticityData[1], this.automaticityData[2] ] ;
    this.chart?.update() ;

    // Calculate status counts for pie chart legend totals
    this.calculateStatusCounts();

    // After we have set the report summary data, we need to update our students in the session
    //this.sessionStorageService.setDiagnosticStudents(students) ;
  }

  /**
   * Get formatted date completed for a specific student and test window
   * @param student Student object
   * @param testWindow Test window to get date for
   * @returns Formatted date string or "-"
   */
  getFormattedDateCompleted(student: Student, testWindow: string): string {
    if (!student.assessmentScores || student.assessmentScores.length === 0) {
      return '-';
    }

    const assessment = student.assessmentScores.find(a => a.testWindow === testWindow);
    if (!assessment) {
      return '-';
    }

    return this.getFormattedDateFromAssessment(assessment);
  }

  /**
   * Get formatted date from an assessment score object
   * @param assessment AssessmentScore object
   * @returns Formatted date string or "-"
   */
  getFormattedDateFromAssessment(assessment: AssessmentScore): string {
    if (assessment.formattedDateCompleted) {
      return assessment.formattedDateCompleted;
    }

    if (assessment.dateCompleted) {
      try {
        return this.utilityService.formatDateNum(assessment.dateCompleted);
      } catch (error) {
        console.warn('Error formatting date:', assessment.dateCompleted, error);
        return '-';
      }
    }

    return '-';
  }
}
